/* EXC cart drawer: discount code box (<exc-cart-discount>, markup in
   snippets/cart-details.liquid)
   Apply sends the code to Shopify's cart (/cart/update.js with discount), then
   asks the theme's cart store to refresh so totals, line prices and the free
   shipping bar all redraw. Shopify answers with each code and whether it
   applies. A code that does not apply is taken back off so it does not sit in
   the cart doing nothing, and the shopper is told. The row underneath lists
   every code (x removes it) plus automatic discounts, with the total saved.
   Styles: assets/exc-cart.css. Notes: hq/projects/excdesigns/theme-build-notes.md */
(function () {
  if (customElements.get('exc-cart-discount')) return;

  var storePromise = null;
  function store() {
    if (!storePromise) storePromise = import('cart-store').then(function (m) { return m.default; });
    return storePromise;
  }

  function money(cents, currency) {
    try {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency || 'USD' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function escapeHtml(t) {
    return String(t).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function sendCodes(codes) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    }).then(function (r) { return r.json(); });
  }

  function currentCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }

  class ExcCartDiscount extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.exc-discount__form');
      this.input = this.querySelector('.exc-discount__input');
      this.button = this.querySelector('.exc-discount__apply');
      this.message = this.querySelector('.exc-discount__message');
      this.applied = this.querySelector('.exc-discount__applied');
      this.list = this.querySelector('.exc-discount__codes');
      this.total = this.querySelector('.exc-discount__total');
      if (!this.form) return;

      this.onSubmit = this.onSubmit.bind(this);
      this.onRemove = this.onRemove.bind(this);
      this.form.addEventListener('submit', this.onSubmit);
      this.list.addEventListener('click', this.onRemove);

      var self = this;
      store().then(function (Cart) {
        self.render(Cart.getState().cart);
        self.unsubscribe = Cart.subscribe(function (state) { self.render(state.cart); });
      }).catch(function () {
        currentCart().then(function (c) { self.render(c); });
      });
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    codesIn(cart) {
      return (cart.discount_codes || []).map(function (d) { return d.code; });
    }

    say(text, isError) {
      this.message.textContent = text || '';
      this.message.hidden = !text;
      this.message.classList.toggle('is-error', !!isError);
    }

    busy(on) {
      this.classList.toggle('is-busy', on);
      this.button.disabled = on;
      this.input.disabled = on;
    }

    refreshTheme() {
      return store().then(function (Cart) { return Cart.refresh(); }).catch(function () {});
    }

    onSubmit(e) {
      e.preventDefault();
      var code = this.input.value.trim();
      if (!code) {
        this.say('Enter a discount code.', true);
        this.input.focus();
        return;
      }
      var self = this;
      this.busy(true);
      this.say('');

      currentCart()
        .then(function (cart) {
          var codes = self.codesIn(cart);
          var already = codes.some(function (c) { return c.toLowerCase() === code.toLowerCase(); });
          if (already) {
            self.say(code.toUpperCase() + ' is already applied.', false);
            return null;
          }
          return sendCodes(codes.concat([code])).then(function (updated) {
            var entry = (updated.discount_codes || []).filter(function (d) {
              return d.code.toLowerCase() === code.toLowerCase();
            })[0];
            if (entry && entry.applicable) {
              self.input.value = '';
              self.say(entry.code.toUpperCase() + ' applied.', false);
              return self.refreshTheme();
            }
            // Not valid for this cart: take it back off
            return sendCodes(codes).then(function () {
              self.say(code.toUpperCase() + ' is not valid for the items in your cart.', true);
              return self.refreshTheme();
            });
          });
        })
        .catch(function () {
          self.say('That code could not be checked. Try again in a moment.', true);
        })
        .then(function () {
          self.busy(false);
        });
    }

    onRemove(e) {
      var btn = e.target.closest('[data-remove-code]');
      if (!btn) return;
      var code = btn.getAttribute('data-remove-code');
      var self = this;
      this.busy(true);
      currentCart()
        .then(function (cart) {
          var rest = self.codesIn(cart).filter(function (c) { return c !== code; });
          return sendCodes(rest);
        })
        .then(function () {
          self.say('');
          return self.refreshTheme();
        })
        .catch(function () {
          self.say('That code could not be removed. Try again.', true);
        })
        .then(function () {
          self.busy(false);
        });
    }

    render(cart) {
      if (!cart) return;
      var chips = [];
      (cart.discount_codes || []).forEach(function (d) {
        if (!d.applicable) return;
        chips.push(
          '<li class="exc-discount__code"><span class="exc-discount__code-text">' + escapeHtml(d.code.toUpperCase()) +
          '</span><button type="button" class="exc-discount__remove" data-remove-code="' + escapeHtml(d.code) +
          '" aria-label="Remove ' + escapeHtml(d.code) + '">&times;</button></li>'
        );
      });
      // Automatic discounts have no code to remove, so only their name shows
      (cart.cart_level_discount_applications || []).forEach(function (a) {
        if (a.type === 'automatic') {
          chips.push('<li class="exc-discount__code"><span class="exc-discount__code-text">' + escapeHtml(a.title) + '</span></li>');
        }
      });
      var saved = cart.total_discount || 0;
      this.list.innerHTML = chips.join('');
      this.total.textContent = saved > 0 ? '-' + money(saved, cart.currency) : '';
      this.applied.hidden = chips.length === 0;
    }
  }

  customElements.define('exc-cart-discount', ExcCartDiscount);
})();
